import React, { useState, useEffect } from "react";
import {
  Grid,
  Card,
  Box,
  Typography,
  InputAdornment,
  Popover,
} from "@mui/material";
import InputField from "../components/InputField/InputField";
import Dropdown from "../components/Dropdown/Dropdown";
import Checkbox from "../components/Checkbox/Checkbox";
import Button from "../components/Button/Button";
import { getEnumOptions } from "./enumUtils";
import AddIcon from "@mui/icons-material/Add";
import { alpha } from "@mui/material/styles";
import DeleteIcon from "@mui/icons-material/Delete";
import ContactsIcon from "@mui/icons-material/Contacts";
import { CheckboxGroup } from "../components/CheckboxGroup/CheckboxGroup";
import {
  mergedRoleOptions,
  updateParentAndChildrenValues,
} from "./mergeRoleSchema";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import DatePicker from "../components/DatePicker/DatePicker";
import PersonIcon from "@mui/icons-material/Person";

const ICON_MAP = {
  Contacts: ContactsIcon,
  Person: PersonIcon,
};

// Create a separate component for the date field
const DateFieldComponent = React.memo(
  ({
    fieldName,
    fieldConfig,
    formData,
    error,
    errorMessage,
    required,
    isEdit,
    setFieldValue,
    setFieldTouched,
    permissions,
  }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [anchorEl, setAnchorEl] = useState(null);

    const handleClick = (event) => {
      setAnchorEl(event.currentTarget);
      setIsOpen(true);
    };

    const handleClose = () => {
      setIsOpen(false);
      setAnchorEl(null);
    };

    const dateValue = formData[fieldName]
      ? new Date(formData[fieldName])
      : null;

    const formattedDate = dateValue
      ? dateValue.toLocaleDateString("en-US", {
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
        })
      : "";

    const handleDateChange = (date) => {
      const formattedValue = date ? date.toISOString().split("T")[0] : null;
      setFieldValue(fieldName, formattedValue);
      setFieldTouched(fieldName, true);
      handleClose();
    };

    const getMinDate = () => {
      const { validation } = fieldConfig;
      if (!validation?.min) return null;

      if (validation.min === "today") {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        return today;
      }
      return formData[validation.min]
        ? new Date(formData[validation.min])
        : null;
    };

    const getMaxDate = () => {
      const { validation } = fieldConfig;
      if (!validation?.max) return null;

      if (validation.max === "today") {
        const today = new Date();
        today.setHours(23, 59, 59, 999);
        return today;
      }
      return formData[validation.max]
        ? new Date(formData[validation.max])
        : null;
    };

    const isProjectManager = (() => {
      if (Array.isArray(fieldConfig?.disableOnRoleId)) {
        return permissions?.projectRoles?.find(
          (projectRole) =>
            parseInt(formData?.project_id) === parseInt(projectRole?.project_id) &&
            fieldConfig.disableOnRoleId.includes(parseInt(projectRole.role?.role_id))
        );
      } else {
        return permissions?.projectRoles?.find(
          (projectRole) =>
            parseInt(formData?.project_id) === parseInt(projectRole?.project_id) &&
            parseInt(projectRole.role?.role_id) === parseInt(fieldConfig?.disableOnRoleId)
        );
      }
    })();
    const superAdminRoleIds = process.env.REACT_APP_SUPER_ADMIN_ROLE_ID
      ? process.env.REACT_APP_SUPER_ADMIN_ROLE_ID.split(",").map((id) =>
          parseInt(id.trim())
        )
      : [];
    const isProjectResource = superAdminRoleIds.includes(
      permissions?.globalRole?.role_id
    );

    const enableBasedOnRole = isProjectResource
      ? false
      : isProjectManager
      ? true
      : false;
    const isDisabled = enableBasedOnRole
      ? true
      : isEdit && fieldConfig.disableOnEdit;

    return (
      <Grid item {...(fieldConfig.size || { xs: 12, sm: 6 })}>
        <InputField
          fullWidth
          name={fieldName}
          label={fieldConfig.label}
          value={formattedDate}
          onClick={!isDisabled ? handleClick : undefined}
          error={!!error}
          errorMessage={errorMessage}
          required={required}
          disabled={isDisabled}
          InputProps={{
            readOnly: true,
            endAdornment: (
              <InputAdornment position="end">
                <CalendarTodayIcon
                  sx={{
                    fontSize: "16px",
                    color: error
                      ? "error.main"
                      : isDisabled
                      ? "action.disabled"
                      : "action.active",
                    opacity: isDisabled ? 0.5 : 1,
                    cursor: isDisabled ? "not-allowed" : "pointer",
                  }}
                  onClick={!isDisabled ? handleClick : undefined}
                />
              </InputAdornment>
            ),
            sx: {
              cursor: isDisabled ? "not-allowed" : "pointer",
              "& .MuiOutlinedInput-input": {
                py: 1,
                pr: "24px",
              },
              "& .MuiInputAdornment-root": {
                mr: 1,
                height: "100%",
                maxHeight: "none",
              },
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: error ? "error.main" : "divider",
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: error ? "error.main" : "primary.main",
              },
            },
          }}
          InputLabelProps={{
            sx: {
              fontSize: "0.875rem",
              color: error ? "error.main" : undefined,
            },
          }}
        />
        <Popover
          open={isOpen}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "left",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "left",
          }}
          PaperProps={{
            sx: { mt: 1 },
          }}
        >
          <DatePicker
            value={dateValue}
            onChange={handleDateChange}
            onClose={handleClose}
            mode="single"
            minDate={getMinDate()}
            maxDate={getMaxDate()}
          />
        </Popover>
      </Grid>
    );
  }
);

const validatePattern = (value, pattern) => {
  if (!pattern || !value) return true;
  const regex = new RegExp(pattern);
  return regex.test(value);
};

/**
 * Renders a form field based on its configuration.
 * - Supports dynamic field visibility (dependsOn).
 * - Handles validation, error display, and required logic.
 * - Supports field types: select, date, checkbox, checkboxGroup, textarea, text, number, email, tel.
 * - Handles role-based disabling for project manager and super admin.
 * - Integrates with custom InputField, Dropdown, Checkbox, CheckboxGroup, and DatePicker components.
 * - Supports dynamic field sets for array/grouped fields.
 * - Handles pattern validation, length, and numeric input restrictions.
 * - Provides custom input handling for capitalization and allowed characters.
 * - Returns a MUI Grid item with the appropriate field component.
 */
export const renderFormField = ({
  fieldName,
  fieldConfig,
  formData,
  errors = {},
  touched = {},
  handleChange,
  handleBlur,
  setFieldValue,
  setFieldTouched,
  options,
  isEdit,
  moduleName,
  permissions,
}) => {
  if (fieldConfig.hidden) {
    return null;
  }
  if (fieldConfig.dependsOn) {
    const { field, condition, value } = fieldConfig.dependsOn;
    const dependentValue = formData[field];

    let shouldShow = false;
    switch (condition) {
      case "hasValue":
        shouldShow = Array.isArray(dependentValue)
          ? dependentValue.some((v) => v !== "" && v != null)
          : Boolean(dependentValue);
        break;
      case "equals":
        shouldShow = dependentValue === value;
        break;
      case "notEquals":
        shouldShow = dependentValue !== value;
        break;
      case "includes":
        shouldShow =
          Array.isArray(value) && value.includes(dependentValue);
        break;
      default:
        shouldShow = false;
    }

    if (!shouldShow) {
      return null;
    }
  }

  const error = touched?.[fieldName] && errors?.[fieldName];
  const errorMessage = error
    ? typeof error === "string"
      ? error
      : Array.isArray(error)
      ? error[0]
      : error?.message || ""
    : "";
  const required = fieldConfig.required;

  const getFieldOptions = () => {
    if (fieldConfig.source === "enum") {
      return getEnumOptions(fieldConfig.enumName || fieldName) || [];
    }
    if (!Array.isArray(options)) {
      return [];
    }

    return options;
  };
  const isProjectManager = (() => {
    if (Array.isArray(fieldConfig?.disableOnRoleId)) {
      return permissions?.projectRoles?.find(
        (projectRole) =>
          parseInt(formData?.project_id) === parseInt(projectRole?.project_id) &&
          fieldConfig.disableOnRoleId.includes(parseInt(projectRole.role?.role_id))
      );
    } else {
      return permissions?.projectRoles?.find(
        (projectRole) =>
          parseInt(formData?.project_id) === parseInt(projectRole?.project_id) &&
          parseInt(projectRole.role?.role_id) === parseInt(fieldConfig?.disableOnRoleId)
      );
    }
  })();
  const superAdminRoleIds = process.env.REACT_APP_SUPER_ADMIN_ROLE_ID
    ? process.env.REACT_APP_SUPER_ADMIN_ROLE_ID.split(",").map((id) =>
        parseInt(id.trim())
      )
    : [];
  const isProjectResource = superAdminRoleIds.includes(
    permissions?.globalRole?.role_id
  );

  const enableBasedOnRole = isProjectResource
    ? false
    : isProjectManager
    ? true
    : false;
  console.log(
    "superAdminRoleIds",
    superAdminRoleIds,
    isProjectResource,
    isProjectManager,
    enableBasedOnRole,
    permissions?.globalRole?.role_id
  );

  const handleInputChange = (e, fieldName, fieldConfig) => {
    let newValue = e.target.value;

    if (fieldConfig.validation?.transform === "capitalize") {
      newValue = newValue.toUpperCase();
    }

    if (fieldConfig.type === "text" && fieldConfig.validation?.pattern) {
      if (newValue === "") {
        handleChange(fieldName, newValue);
        return;
      }

      if (fieldConfig.validation?.length) {
        if (newValue.length > fieldConfig.validation.length) {
          return;
        }
        if (/^[A-Za-z]*$/.test(newValue)) {
          handleChange(fieldName, newValue);
        }
        return;
      }

      const pattern = new RegExp(
        fieldConfig.validation.pattern.charAt(0) === "^"
          ? fieldConfig.validation.pattern.slice(1)
          : fieldConfig.validation.pattern
      );

      if (pattern.test(newValue)) {
        handleChange(fieldName, newValue);
      }
      return;
    }

    if (fieldConfig.type === "number") {
      if (fieldConfig.inputProps?.step && fieldConfig.inputProps.step !== "1") {
        const decimalPlaces = fieldConfig.inputProps.step.includes(".")
          ? fieldConfig.inputProps.step.split(".")[1].length
          : 0;

        const decimalPattern = new RegExp(`^\\d*\\.?\\d{0,${decimalPlaces}}$`);

        if (newValue === "" || decimalPattern.test(newValue)) {
          handleChange(fieldName, newValue);
        }
        return;
      } else {
        if (newValue === "" || /^\d*$/.test(newValue)) {
          handleChange(fieldName, newValue);
        }
        return;
      }
    }

    if (fieldConfig.type === "tel") {
      // Allow digits, +, -, (), and spaces only (no length limit)
      if (newValue === "" || /^[0-9+\-() ]*$/.test(newValue)) {
        handleChange(fieldName, newValue);
      }
      return;
    }

    if (fieldConfig.type === "email") {
      handleChange(fieldName, newValue);
      return;
    }

    if (fieldConfig.validation?.pattern) {
      if (newValue === "") {
        handleChange(fieldName, newValue);
        return;
      }
      const lastChar = newValue.slice(-1);
      const pattern = new RegExp(
        fieldConfig.validation.pattern.charAt(0) === "^"
          ? fieldConfig.validation.pattern.slice(1)
          : fieldConfig.validation.pattern
      );

      if (pattern.test(lastChar)) {
        handleChange(fieldName, newValue);
      }
      return;
    }
    handleChange(fieldName, newValue);
  };

  if (fieldConfig.dynamic && fieldConfig.fields) {
    return (
      <Grid item xs={12}>
        <DynamicFieldSet
          fieldName={fieldName}
          fieldConfig={fieldConfig}
          formData={formData}
          errors={errors}
          touched={touched}
          handleChange={handleChange}
          handleBlur={handleBlur}
          setFieldValue={setFieldValue}
          setFieldTouched={setFieldTouched}
          options={options}
          isEdit={isEdit}
        />
      </Grid>
    );
  }

  switch (fieldConfig.type) {
    case "select":
      return (
        <Grid item {...(fieldConfig.size || { xs: 12, sm: 6 })}>
          <Dropdown
            name={fieldName}
            label={fieldConfig.label}
            options={getFieldOptions()}
            value={formData[fieldName] || (fieldConfig.multiple ? [] : "")}
            onChange={(value) => {
              handleChange(fieldName, value);
            }}
            onBlur={handleBlur}
            placeholder={fieldConfig.placeholder}
            error={!!error}
            errorMessage={errorMessage}
            required={fieldConfig.required}
            maxSelected={fieldConfig.max}
            disabled={
              enableBasedOnRole
                ? true
                : isEdit && fieldConfig.disableOnEdit
                ? fieldConfig.disableOnEdit
                : !isEdit && fieldConfig.disableOnAdd
                ? fieldConfig.disableOnAdd
                : fieldConfig.disabled
                ? true
                : false
            }
            multiple={fieldConfig.multiple}
            fullWidth
          />
        </Grid>
      );

    case "date":
      return (
        <DateFieldComponent
          fieldName={fieldName}
          fieldConfig={fieldConfig}
          formData={formData}
          error={error}
          errorMessage={errorMessage}
          required={fieldConfig.required}
          isEdit={isEdit}
          setFieldValue={setFieldValue}
          setFieldTouched={setFieldTouched}
          permissions={permissions}
        />
      );

    case "checkbox":
      return (
        <Grid item {...(fieldConfig.size || { xs: 12 })}>
          <Checkbox
            name={fieldName}
            label={fieldConfig.label}
            checked={formData[fieldName] || false}
            onChange={(e) => {
              if (
                fieldConfig.dependable &&
                formData[fieldConfig.dependable.key]
              ) {
                handleChange(
                  fieldConfig.dependable.key,
                  fieldConfig.dependable.value
                );
              }
              handleChange(fieldName, e.target.checked);
            }}
            onBlur={handleBlur}
            error={!!error}
            errorMessage={errorMessage}
            required={fieldConfig.required}
            disabled={
              isEdit && fieldConfig.disableOnEdit
                ? fieldConfig.disableOnEdit
                : !isEdit && fieldConfig.disableOnAdd
                ? fieldConfig.disableOnAdd
                : false
            }
          />
        </Grid>
      );

    case "checkboxGroup":
      let checkGroupOptions = mergedRoleOptions(
        moduleName,
        formData[fieldName] || [],
        formData
      );

      return (
        <Grid
          container
          spacing={2}
          sx={{
            width: "100%",
            m: 0,
            "& > .MuiGrid-item": {
              width: (props) => (props.size?.sm === 12 ? "100%" : undefined),
              maxWidth: (props) => (props.size?.sm === 12 ? "100%" : undefined),
            },
          }}
        >
          <CheckboxGroup
            label={fieldConfig.label}
            handleChange={handleChange}
            fieldName={fieldName}
            setFieldValue={setFieldValue}
            setFieldTouched={setFieldTouched}
            data={checkGroupOptions ?? []}
            errors={errors}
            required={fieldConfig.required}
          />
        </Grid>
      );

    case "textarea":
      return (
        <Grid item {...(fieldConfig.size || { xs: 12 })}>
          <InputField
            fullWidth
            multiline
            rows={4}
            name={fieldName}
            label={fieldConfig.label}
            value={formData[fieldName] || ""}
            onChange={(e) => {
              handleChange(fieldName, e.target.value);
            }}
            onBlur={handleBlur}
            placeholder={fieldConfig.placeholder}
            error={!!error}
            errorMessage={errorMessage}
            required={fieldConfig.required}
            disabled={
              enableBasedOnRole
                ? true
                : isEdit && fieldConfig.disableOnEdit
                ? fieldConfig.disableOnEdit
                : !isEdit && fieldConfig.disableOnAdd
                ? fieldConfig.disableOnAdd
                : false
            }
            inputProps={{
              ...fieldConfig.inputProps,
              autoComplete: "off",
            }}
          />
        </Grid>
      );

    case "text":
    case "number":
    case "email":
    case "tel":
    default:
      return (
        <Grid item {...(fieldConfig.size || { xs: 12, sm: 6 })}>
          <InputField
            fullWidth
            name={fieldName}
            label={fieldConfig.label}
            value={formData[fieldName] || ""}
            onChange={(e) => handleInputChange(e, fieldName, fieldConfig)}
            onBlur={handleBlur}
            placeholder={fieldConfig.placeholder}
            error={!!error}
            errorMessage={errorMessage}
            required={fieldConfig.required}
            disabled={
              enableBasedOnRole
                ? true
                : isEdit && fieldConfig.disableOnEdit
                ? fieldConfig.disableOnEdit
                : !isEdit && fieldConfig.disableOnAdd
                ? fieldConfig.disableOnAdd
                : false
            }
            type={fieldConfig.type === "tel" ? "text" : fieldConfig.type}
            sx={{ width: "100%" }}
            inputProps={{
              ...fieldConfig.inputProps,
              autoComplete: "off",
            }}
            InputProps={{
              ...fieldConfig.InputProps,
              sx: {
                "& .MuiOutlinedInput-input": { py: 1 },
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: error ? "error.main" : "divider",
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: error ? "error.main" : "primary.main",
                },
              },
            }}
            InputLabelProps={{
              sx: {
                fontSize: "0.875rem",
                transform: "translate(14px, 9px) scale(1)",
                color: error ? "error.main" : undefined,
              },
            }}
          />
        </Grid>
      );
  }
};

const DynamicFieldSet = ({
  fieldName,
  fieldConfig,
  formData,
  errors = {},
  touched = {},
  handleChange,
  handleBlur,
  setFieldValue,
  setFieldTouched,
  options,
  isEdit,
}) => {
  const [fieldSets, setFieldSets] = useState([{ id: 0 }]);

  useEffect(() => {
    const newCount =
      formData[fieldName]?.[Object.keys(fieldConfig.fields)[0]]?.length || 1;
    setFieldSets(
      Array.from({ length: newCount }, (_, index) => ({ id: index }))
    );
  }, [formData, fieldName, fieldConfig.fields]);

  const handleAddField = () => {
    const newIndex = fieldSets.length;

    // Add empty values for all subfields
    Object.keys(fieldConfig.fields).forEach((subFieldName) => {
      const currentValues = formData[fieldName]?.[subFieldName] || [];
      setFieldValue(`${fieldName}.${subFieldName}`, [...currentValues, ""]);
    });

    setFieldSets([...fieldSets, { id: newIndex }]);
  };

  const handleRemoveField = (index) => {
    // Remove values for all subfields at the specified index
    Object.keys(fieldConfig.fields).forEach((subFieldName) => {
      const currentValues = formData[fieldName]?.[subFieldName] || [];
      const newValues = currentValues.filter((_, i) => i !== index);
      setFieldValue(`${fieldName}.${subFieldName}`, newValues);
    });
  };

  const handleFieldValueChange = (
    subFieldName,
    index,
    value,
    subFieldConfig
  ) => {
    let newValue = value;

    // if (subFieldConfig.validation?.transform === "capitalize") {
    //   newValue = value
    //     .split(" ")
    //     .map(
    //       (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
    //     )
    //     .join(" ");
    // }

    if (subFieldConfig.type === "tel") {
      // Allow digits, +, -, (), and spaces only (no length limit)
      if (newValue === "" || /^[0-9+\-() ]*$/.test(newValue)) {
        const currentValues = formData[fieldName]?.[subFieldName] || [];
        const newValues = [...currentValues];
        newValues[index] = newValue;
        setFieldValue(`${fieldName}.${subFieldName}`, newValues);
        setFieldTouched(`${fieldName}.${subFieldName}`, true, true);
      }
      return;
    }

    if (subFieldConfig.type === "email") {
      const currentValues = formData[fieldName]?.[subFieldName] || [];
      const newValues = [...currentValues];
      newValues[index] = newValue;
      setFieldValue(`${fieldName}.${subFieldName}`, newValues);
      setFieldTouched(`${fieldName}.${subFieldName}`, true, true);
      return;
    }

    if (subFieldConfig.validation?.pattern) {
      if (newValue === "") {
        const currentValues = formData[fieldName]?.[subFieldName] || [];
        const newValues = [...currentValues];
        newValues[index] = newValue;
        setFieldValue(`${fieldName}.${subFieldName}`, newValues);
        setFieldTouched(`${fieldName}.${subFieldName}`, true, true);
        return;
      }

      const lastChar = newValue.slice(-1);
      const pattern = new RegExp(
        subFieldConfig.validation.pattern.charAt(0) === "^"
          ? subFieldConfig.validation.pattern.slice(1)
          : subFieldConfig.validation.pattern
      );

      if (pattern.test(lastChar)) {
        const currentValues = formData[fieldName]?.[subFieldName] || [];
        const newValues = [...currentValues];
        newValues[index] = newValue;
        setFieldValue(`${fieldName}.${subFieldName}`, newValues);
        setFieldTouched(`${fieldName}.${subFieldName}`, true, true);
      }
      return;
    }
    const currentValues = formData[fieldName]?.[subFieldName] || [];
    const newValues = [...currentValues];
    newValues[index] = newValue;
    setFieldValue(`${fieldName}.${subFieldName}`, newValues);
    setFieldTouched(`${fieldName}.${subFieldName}`, true, true);
  };

  const hasFieldError = (subFieldName, index) => {
    if (!fieldConfig.fields[subFieldName].required) {
      return false;
    }

    const fieldErrors = errors[fieldName]?.[subFieldName];
    const fieldTouched = touched[fieldName]?.[subFieldName];
    return fieldTouched?.[index] && fieldErrors?.[index];
  };

  const getFieldError = (subFieldName, index) => {
    if (!fieldConfig.fields[subFieldName].required) {
      return "";
    }

    const fieldErrors = errors[fieldName]?.[subFieldName];
    const fieldTouched = touched[fieldName]?.[subFieldName];
    return fieldTouched?.[index] && fieldErrors?.[index]
      ? fieldErrors[index]
      : "";
  };

  const getFieldOptions = (subFieldConfig) => {
    if (!subFieldConfig.source || !options) {
      console.warn("Missing source or options:", {
        source: subFieldConfig.source,
        options,
      });
      return [];
    }
    const sourceOptions = options[subFieldConfig.source];
    if (!Array.isArray(sourceOptions)) {
      return [];
    }
    return sourceOptions;
  };

  const IconComponent = ICON_MAP[fieldConfig.icon] || ContactsIcon;

  const renderField = (subFieldName, subFieldConfig, index) => {
    if (subFieldName === "client_poc_id") {
      return null;
    }

    return (
      <Grid
        item
        {...(subFieldConfig.size || { xs: 12, sm: 6 })}
        key={subFieldName}
      >
        {subFieldConfig.type === "select" ? (
          <Dropdown
            name={`${fieldName}.${subFieldName}.${index}`}
            label={subFieldConfig.label}
            options={getFieldOptions(subFieldConfig)}
            value={
              formData[fieldName]?.[subFieldName]?.[index] ||
              (subFieldConfig.multiple ? [] : "")
            }
            onChange={(value) =>
              handleFieldValueChange(subFieldName, index, value, subFieldConfig)
            }
            onBlur={() =>
              setFieldTouched(`${fieldName}.${subFieldName}`, true, true)
            }
            error={hasFieldError(subFieldName, index)}
            errorMessage={getFieldError(subFieldName, index)}
            required={subFieldConfig.required}
            placeholder={subFieldConfig.placeholder}
            multiple={subFieldConfig.multiple}
            fullWidth
            size="small"
          />
        ) : (
          <InputField
            fullWidth
            size="small"
            name={`${fieldName}.${subFieldName}.${index}`}
            label={subFieldConfig.label}
            value={formData[fieldName]?.[subFieldName]?.[index] || ""}
            onChange={(e) =>
              handleFieldValueChange(
                subFieldName,
                index,
                e.target.value,
                subFieldConfig
              )
            }
            onBlur={handleBlur}
            error={hasFieldError(subFieldName, index)}
            errorMessage={getFieldError(subFieldName, index)}
            required={subFieldConfig.required}
            type={subFieldConfig.type === "tel" ? "text" : subFieldConfig.type}
            placeholder={subFieldConfig.placeholder}
            inputProps={{
              autoComplete: "off",
            }}
          />
        )}
      </Grid>
    );
  };

  return (
    <Box
      sx={{
        width: "100%",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 0.5,
          pb: 0.5,
          pt: 0.75,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <IconComponent sx={{ fontSize: 14 }} />
          <Typography
            sx={{
              fontSize: "12px",
              fontWeight: 600,
              color: (theme) => theme.palette.text.primary,
            }}
          >
            {`${fieldConfig.title} (${fieldSets.length})`}
          </Typography>
        </Box>
        <Button
          variant="outlined"
          size="small"
          startIcon={<AddIcon sx={{ fontSize: 14 }} />}
          onClick={handleAddField}
          disabled={fieldSets.length >= (fieldConfig.maxItems || 5)}
          sx={{
            minHeight: "18px",
            padding: "1px 4px",
            fontSize: "11px",
            "& .MuiButton-startIcon": {
              marginRight: "2px",
            },
          }}
        >
          {fieldConfig.addButtonText}
        </Button>
      </Box>

      <Box sx={{ pt: 1 }}>
        {fieldSets.map((set, index) => (
          <Box
            key={set.id}
            sx={{
              mb: index !== fieldSets.length - 1 ? 1 : 0,
              border: "1px solid",
              borderColor: "divider",
              borderRadius: "5px",
              py: 0.75,
              px: 1.5,
            }}
          >
            <Box
              sx={{
                pb: 1,
                mb: 1,
                mx: -1.5,
                px: 1.5,
                borderBottom: "1px solid",
                borderColor: "divider",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography
                sx={{
                  fontSize: "13px",
                  fontWeight: 600,
                  color: (theme) => theme.palette.text.primary,
                }}
              >
                {`${fieldConfig.itemTitle} ${index + 1}`}
              </Typography>

              {fieldSets.length > 1 && (
                <DeleteIcon
                  sx={{
                    fontSize: 18,
                    color: "error.main",
                    cursor: "pointer",
                    opacity: 0.7,
                    transition: "opacity 0.2s",
                    "&:hover": {
                      opacity: 1,
                    },
                  }}
                  onClick={() => handleRemoveField(index)}
                />
              )}
            </Box>

            <Grid container spacing={1.5}>
              {Object.entries(fieldConfig.fields).map(
                ([subFieldName, subFieldConfig]) =>
                  renderField(subFieldName, subFieldConfig, index)
              )}
            </Grid>
          </Box>
        ))}
      </Box>
    </Box>
  );
};
